"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens } from "@/lib/api";

const REPLY_MAX = 1000;

/**
 * EPIC-044 part 4d — reply composer for the discussion detail page.
 *
 * Auth-required. Click the textarea → if not signed in, redirect to
 * /login?next=<current path>. Submit → POST /v1/discussions/:id/replies
 * → router.refresh so the SSR rendered list re-fetches with the new
 * row included.
 */
export function ReplyComposer({ discussionId }: { discussionId: string }) {
  const { t } = useTranslation();
  const router = useRouter();
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    setSignedIn(loadTokens() !== null);
  }, []);

  if (signedIn === null) return null;
  if (!signedIn) {
    return (
      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-3 text-[12px] text-[var(--color-text2)]">
        <a
          href={`/login?next=${encodeURIComponent(typeof window !== "undefined" ? window.location.pathname : "")}`}
          style={{ color: "var(--color-brand)" }}
          className="font-bold hover:underline"
        >
          {t("replyComposer.signIn")}
        </a>
        {t("replyComposer.toReplySuffix")}
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || body.trim().length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.createDiscussionReply(discussionId, { body: body.trim() });
      setBody("");
      router.refresh();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("replyComposer.errPostFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, REPLY_MAX))}
        rows={3}
        maxLength={REPLY_MAX}
        placeholder={t("replyComposer.placeholder")}
        className="w-full rounded border border-[var(--color-border)] bg-transparent px-3 py-2 text-[13px] text-[var(--color-text)] focus:border-[var(--color-brand)] focus:outline-none"
      />
      {error && (
        <p className="mt-2 text-[11px] text-red-400">{error}</p>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] text-[var(--color-text3)]">
          {body.length}/{REPLY_MAX}
        </span>
        <button
          type="submit"
          disabled={submitting || body.trim().length === 0}
          className="min-h-[36px] rounded-md px-3 py-1.5 text-[11px] font-bold text-white disabled:opacity-50 md:min-h-0"
          style={{ background: "var(--color-brand)" }}
        >
          {submitting
            ? t("replyComposer.posting")
            : t("replyComposer.submit")}
        </button>
      </div>
    </form>
  );
}
